import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function normalizeColorKey(value) {
  return value?.trim().toLowerCase().replace(/[\s_-]+/g, '') ?? '';
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#applyColorAltFilter();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (event.detail.resource && newMediaGallery instanceof HTMLElement) {
      const optionPosition = newMediaGallery.dataset.colorOptionPosition;
      if (optionPosition) {
        const colorKey = event.detail.resource[`option${optionPosition}`];
        if (typeof colorKey === 'string' && colorKey) {
          newMediaGallery.dataset.selectedColorKey = colorKey;
        }
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * @param {object | undefined} variant
   */
  #applyColorAltFilter(variant) {
    if (this.dataset.colorAltFilter !== 'true') return;

    const optionPosition = this.dataset.colorOptionPosition;
    if (!optionPosition) return;

    const colorKey =
      variant?.[`option${optionPosition}`] ??
      this.dataset.selectedColorKey ??
      this.#getSelectedColorFromPicker(optionPosition);

    if (!colorKey) return;

    const normalizedSelected = normalizeColorKey(colorKey);
    const slides = this.querySelectorAll('slideshow-slide');

    for (const slide of slides) {
      const taggedMedia = slide.querySelector('[data-color-alt-tags]');
      const tags = taggedMedia?.dataset.colorAltTags ?? 'general';
      const isVisible = this.#slideMatchesColor(tags, normalizedSelected);
      slide.toggleAttribute('hidden', !isVisible);
      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }

    const firstVisible = this.querySelector('slideshow-slide:not([hidden])');
    const firstMediaId = firstVisible?.querySelector('[data-media-id]')?.getAttribute('data-media-id');

    if (firstMediaId && this.slideshow) {
      this.slideshow.select({ id: firstMediaId }, undefined, { animate: false });
    }
  }

  /**
   * @param {string} optionPosition
   * @returns {string | undefined}
   */
  #getSelectedColorFromPicker(optionPosition) {
    const fieldsetIndex = Number(optionPosition) - 1;
    const picker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    const fieldset = picker?.querySelector(`fieldset[data-fieldset-index="${fieldsetIndex}"]`);
    const checked = fieldset?.querySelector('input:checked');

    return checked?.value;
  }

  /**
   * @param {string} tags
   * @param {string} normalizedSelected
   * @returns {boolean}
   */
  #slideMatchesColor(tags, normalizedSelected) {
    if (!tags || tags === 'general') return true;

    return tags.split(',').some((tag) => normalizeColorKey(tag) === normalizedSelected);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
